import {
  Goal,
  GoalGroupBy,
  GoalProgressCount,
  GoalSortingMode,
  GroupByMode,
  ID,
  LifeArea,
  lifeAreaOtherID,
} from '@griply/types';

import { getInheritedLifeAreaIds } from '../derived-goal-state';
import { sortGoals } from '../sort-goals';
import {
  compareDeadlineQuarterKeys,
  getDeadlineQuarterKey,
} from './deadline-quarters';
import { getNearestPresentParentId } from './get-nearest-present-parent-id';

export enum GoalCategoryKind {
  LifeArea = 'life-area',
  Quarter = 'quarter',
  Ungrouped = 'ungrouped',
}

export type GoalCategoryNode = {
  id: ID;
  goal: Goal;
  subCategories: GoalCategoryNode[];
};

export type GoalCategory = {
  id: ID;
  kind: GoalCategoryKind;
  lifeArea?: LifeArea;
  quarterKey?: string;
  subCategories: GoalCategoryNode[];
};

export type BuildGoalCategoryTreeOptions = {
  goals: Goal[];
  // Unfiltered set: a sub-goal keeps its inherited life area when a filter drops its parent.
  allGoals: Goal[];
  lifeAreas: LifeArea[];
  groupBy?: GoalGroupBy;
  // Canonical order of every goal at every depth, as `useGoalsOverviewOrder` builds it.
  customOrder?: ID[] | null;
  // Open tasks per goal; the Tasks sort mode no-ops without it.
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
};

export const buildGoalCategoryTree = ({
  goals,
  allGoals,
  lifeAreas,
  groupBy,
  customOrder,
  taskCounts,
  goalProgressCounts,
}: BuildGoalCategoryTreeOptions): GoalCategory[] => {
  const lifeAreaIds = getInheritedLifeAreaIds(allGoals);
  const nodesById = new Map<ID, GoalCategoryNode>();

  goals.forEach((goal) => {
    nodesById.set(goal.id, { id: goal.id, goal, subCategories: [] });
  });

  const getPresentParentId = (goal: Goal) =>
    getNearestPresentParentId(goal, (id) => nodesById.has(id));

  goals.forEach((goal) => {
    const parentId = getPresentParentId(goal);
    if (!parentId) {
      return;
    }
    const parentNode = nodesById.get(parentId);
    const node = nodesById.get(goal.id);
    if (parentNode && node) {
      parentNode.subCategories.push(node);
    }
  });

  const sortChildren = (
    subCategories: GoalCategoryNode[],
    parentGoal: Goal,
  ): GoalCategoryNode[] =>
    sortGoals(
      subCategories.map(({ goal }) => goal),
      {
        allGoals: goals,
        sortMode: customOrder
          ? GoalSortingMode.Custom
          : parentGoal.goalSortingMode,
        customOrder: customOrder ?? parentGoal.goalSorting,
        taskCounts,
        goalProgressCounts,
      },
    )
      .map(({ id }) => nodesById.get(id))
      .filter((node): node is GoalCategoryNode => !!node)
      .map((node) => ({
        ...node,
        subCategories: sortChildren(node.subCategories, node.goal),
      }));

  const toNodes = (categoryGoals: Goal[]): GoalCategoryNode[] =>
    categoryGoals
      .map(({ id }) => nodesById.get(id))
      .filter((node): node is GoalCategoryNode => !!node)
      .map((node) => ({
        ...node,
        subCategories: sortChildren(node.subCategories, node.goal),
      }));

  const orderRoots = (roots: Goal[], lifeAreaSorting?: ID[] | null) =>
    sortGoals(roots, {
      allGoals: goals,
      sortMode: GoalSortingMode.Custom,
      customOrder: customOrder ?? lifeAreaSorting,
    });

  const rootGoals = goals.filter((goal) => !getPresentParentId(goal));

  // deadline
  if (groupBy === GroupByMode.Deadline) {
    const goalsByQuarter = new Map<string, Goal[]>();
    orderRoots(rootGoals).forEach((goal) => {
      const key = getDeadlineQuarterKey(goal.deadline);
      goalsByQuarter.set(key, [...(goalsByQuarter.get(key) ?? []), goal]);
    });

    return [...goalsByQuarter.keys()]
      .sort(compareDeadlineQuarterKeys)
      .map((quarterKey) => ({
        id: quarterKey,
        kind: GoalCategoryKind.Quarter,
        quarterKey,
        subCategories: toNodes(goalsByQuarter.get(quarterKey) ?? []),
      }));
  }

  // no grouping
  if (groupBy !== GroupByMode.LifeArea) {
    return [
      {
        id: lifeAreaOtherID,
        kind: GoalCategoryKind.Ungrouped,
        subCategories: toNodes(orderRoots(rootGoals)),
      },
    ];
  }

  const knownLifeAreaIds = new Set(lifeAreas.map(({ id }) => id));

  const categories: GoalCategory[] = lifeAreas.map((lifeArea) => {
    const lifeAreaGoals = rootGoals.filter(
      (goal) => lifeAreaIds.get(goal.id) === lifeArea.id,
    );

    return {
      id: lifeArea.id,
      kind: GoalCategoryKind.LifeArea,
      lifeArea,
      subCategories: lifeAreaGoals.length
        ? toNodes(orderRoots(lifeAreaGoals, lifeArea.goalSorting))
        : [],
    };
  });

  // A goal whose life area was archived or filtered away still needs a home.
  const orphanRoots = rootGoals.filter((goal) => {
    const lifeAreaId = lifeAreaIds.get(goal.id);
    return !lifeAreaId || !knownLifeAreaIds.has(lifeAreaId);
  });

  if (orphanRoots.length) {
    categories.push({
      id: lifeAreaOtherID,
      kind: GoalCategoryKind.LifeArea,
      subCategories: toNodes(orderRoots(orphanRoots)),
    });
  }

  return categories;
};
